import {getTestProps} from '@benchling/js-test-utils';
import {partition} from '@benchling/js-utils';
import * as d3 from 'd3';
import React from 'react';

import DisplayInfoLabel from 'molbio/components/visualizations/DisplayInfoLabel';
import {
  PolymerPreviewOverlay,
  VisualPolymer,
  VisualSequenceCutSite,
  VisualPolymerFeature,
} from 'molbio/components/visualizations/PolymerPreviewConstants';
import {
  getEditOverlayRenderParams,
  getLinearPointForIndex,
} from 'molbio/components/visualizations/VisualizationUtil';
import {getEnzyme} from 'molbio/enzymes/EnzymeUtil';
import {axisSpacing} from 'molbio/maps/circular/CircularAxisUtil';
import {formatAnnotationDisplayName, getAnnotationPathParams} from 'molbio/maps/linear/AnnotationsHelpers';
import {getFeatureLayers} from 'molbio/maps/MapsUtil';
import * as sequtil from 'molbio/SeqUtil';
import {getLinearHomologyStripeData} from 'molbio/visualization/FragmentVisualizationUtil';
import {FONT_FAMILY_MONOSPACED} from 'styles/FontConstants';
import {GRAY_LIGHT, NEAR_BLACK} from 'ui-library/Colors';
import Line from 'ui-library/svg/Line';
import {Point} from 'util/geometry/GeometryConstants';

const WIDTH = 300;
const AXIS_Y_OFFSET = 75;
const AXIS_X_MARGIN = 25;
const AXIS_LENGTH = WIDTH - 2 * AXIS_X_MARGIN;
const AXIS_THICKNESS = 15;
const AXIS_NUMBER_TICK_LENGTH = 4;
const AXIS_LEFT_POINT = {x: AXIS_X_MARGIN, y: AXIS_Y_OFFSET};
const AXIS_RIGHT_POINT = {x: WIDTH - AXIS_X_MARGIN, y: AXIS_Y_OFFSET};
const ANNOTATION_THICKNESS = 12;
const ANNOTATIONS_Y_OFFSET = AXIS_Y_OFFSET + AXIS_THICKNESS + AXIS_NUMBER_TICK_LENGTH + 12;
const ANNOTATION_LABEL_PADDING = 4;
const EDIT_OVERLAY_HEIGHT = 42;
const DELETE_OVERLAY_FRAGMENT_MARGIN = 4;
const AXIS_BOTTOM_OFFSET = Math.ceil(AXIS_THICKNESS / 2);
const UPPER_CUTSITE_Y = 35;
const LOWER_CUTSITE_Y = 55;

const DEFAULT_ANNOTATION_RENDER_PARAMS = {
  height: ANNOTATION_THICKNESS,
  arrowsOffset: 6,
  arrowWidth: 3,
  arrowSpacing: 3,
  labelColor: NEAR_BLACK,
  outsideLabelColor: NEAR_BLACK,
};

function getLinearPointForIndexOnPreview(index: number, length: number, yOffset: number = 0): Point {
  return getLinearPointForIndex(index, length, AXIS_LEFT_POINT, AXIS_RIGHT_POINT, yOffset);
}

/**
 * Renders approximately 4 axis numbering labels and tick marks on a linear preview.
 */
function AxisNumbering({length, startIndex}: {length: number; startIndex: number}): JSX.Element {
  const step = axisSpacing(length, 4);
  const firstTick = Math.ceil(startIndex / step) * step;
  const labels = d3.range(firstTick, sequtil.modelToDisplay(length, startIndex), step);
  return (
    <>
      {labels.map((label) => {
        const modelIndex = sequtil.displayToModel(label, startIndex);
        const tickStartPoint = getLinearPointForIndexOnPreview(modelIndex, length, AXIS_BOTTOM_OFFSET);
        const tickEndPoint = getLinearPointForIndexOnPreview(
          modelIndex,
          length,
          AXIS_BOTTOM_OFFSET + AXIS_NUMBER_TICK_LENGTH
        );
        return (
          <g
            key={`axis-label-${label}`}
            {...getTestProps({component: 'LinearPolymerPreview', element: 'axisLabel', key: String(label)})}
          >
            <Line
              dataTestAttrs={{component: 'LinearPolymerPreview', element: 'axisTick', key: String(label)}}
              startPoint={tickStartPoint}
              endPoint={tickEndPoint}
              stroke={NEAR_BLACK}
              strokeWidth={2}
            />
            <text
              {...getTestProps({
                component: 'LinearPolymerPreview',
                element: 'axisLabelText',
                key: String(label),
              })}
              x={tickEndPoint.x}
              y={tickEndPoint.y + 2}
              fontWeight={700}
              fontSize={10}
              dominantBaseline="hanging"
              textAnchor="middle"
            >
              {label}
            </text>
          </g>
        );
      })}
    </>
  );
}

/**
 * Renders a single linear annotation with a name label inside if it fits.
 */
function Annotation({
  annotation,
  layer,
  seqLength,
}: {
  annotation: VisualPolymerFeature;
  seqLength: number;
  layer: number;
}): JSX.Element {
  const {start, end, strand, color, name} = annotation;
  const startPoint = getLinearPointForIndexOnPreview(start, seqLength);
  const endPoint = getLinearPointForIndexOnPreview(sequtil.modelEndToRender(end, seqLength), seqLength);
  const width = endPoint.x - startPoint.x;
  const tipWidth = strand === 0 ? 0 : Math.min(width, 8);
  const annotationRenderParams = {
    ...DEFAULT_ANNOTATION_RENDER_PARAMS,
    color,
    start,
    end,
    width,
    tipWidth,
  };
  const {
    fillD,
    strokeLeftD,
    strokeMiddleD,
    strokeRightD,
    strokeLeftRender,
    strokeRightRender,
    strokeMiddleRender,
  } = getAnnotationPathParams(annotationRenderParams, 0, seqLength, strand);
  const availableSpace = width - tipWidth - ANNOTATION_LABEL_PADDING * 2;
  const displayText = formatAnnotationDisplayName(name, availableSpace / 5);

  return (
    <g
      {...getTestProps({component: 'LinearPolymerPreview', element: 'annotation', key: name})}
      transform={`translate(${startPoint.x}, ${ANNOTATIONS_Y_OFFSET + layer * (ANNOTATION_THICKNESS + 2)})`}
    >
      <path
        {...getTestProps({component: 'LinearPolymerPreview', element: 'annotationFill', key: name})}
        d={fillD}
        fill={color}
        fillOpacity={0.7}
      />
      <path
        d={strokeLeftD}
        fill="none"
        stroke={d3.rgb(color).darker().formatHex()}
        shapeRendering={strokeLeftRender}
      />
      <path
        d={strokeRightD}
        fill="none"
        stroke={d3.rgb(color).darker().formatHex()}
        shapeRendering={strokeRightRender}
      />
      <path
        d={strokeMiddleD}
        fill="none"
        stroke={d3.rgb(color).darker().formatHex()}
        shapeRendering={strokeMiddleRender}
      />
      <text
        {...getTestProps({component: 'LinearPolymerPreview', element: 'annotationText', key: name})}
        x={(width - tipWidth) / 2}
        y={ANNOTATION_THICKNESS / 2 + 1}
        fontWeight={500}
        fontSize={10}
        dominantBaseline="middle"
        textAnchor="middle"
      >
        {displayText}
      </text>
    </g>
  );
}

/**
 * Renders visuals representing edit regions on a circular preview.
 */
function EditOverlay({overlay, seqLength}: {overlay: PolymerPreviewOverlay; seqLength: number}): JSX.Element {
  const {start, end} = overlay;
  const startPoint = getLinearPointForIndexOnPreview(start, seqLength, AXIS_BOTTOM_OFFSET);
  const endPoint = getLinearPointForIndexOnPreview(
    sequtil.modelEndToRender(end, seqLength),
    seqLength,
    AXIS_BOTTOM_OFFSET
  );
  const {showFragmentOutside, fragmentColor, boundaryLineColor, backgroundColor} = getEditOverlayRenderParams(
    overlay,
    seqLength
  );
  const solidFragmentStartPoint = showFragmentOutside
    ? {x: startPoint.x + DELETE_OVERLAY_FRAGMENT_MARGIN, y: startPoint.y - 34}
    : {x: startPoint.x, y: startPoint.y - AXIS_BOTTOM_OFFSET};
  const solidFragmentEndPoint = showFragmentOutside
    ? {x: endPoint.x - DELETE_OVERLAY_FRAGMENT_MARGIN, y: endPoint.y - 34}
    : {x: endPoint.x, y: endPoint.y - AXIS_BOTTOM_OFFSET};

  return (
    <g {...getTestProps({component: 'LinearPolymerPreview', element: 'editOverlay'})}>
      {backgroundColor && (
        <rect
          {...getTestProps({component: 'LinearPolymerPreview', element: 'editOverlayBackground'})}
          x={startPoint.x}
          y={AXIS_Y_OFFSET + AXIS_BOTTOM_OFFSET - EDIT_OVERLAY_HEIGHT}
          width={endPoint.x - startPoint.x}
          height={EDIT_OVERLAY_HEIGHT}
          fill={backgroundColor}
        />
      )}
      {fragmentColor && (
        <Line
          dataTestAttrs={{component: 'LinearPolymerPreview', element: 'editOverlayFragment'}}
          startPoint={solidFragmentStartPoint}
          endPoint={solidFragmentEndPoint}
          strokeWidth={AXIS_THICKNESS}
          stroke={fragmentColor}
        />
      )}
      {boundaryLineColor && (
        <>
          <Line
            dataTestAttrs={{component: 'LinearPolymerPreview', element: 'editOverlayBoundaryLine'}}
            startPoint={startPoint}
            endPoint={{x: startPoint.x, y: startPoint.y - EDIT_OVERLAY_HEIGHT}}
            stroke={boundaryLineColor}
            strokeWidth={3}
            strokeLinecap="round"
          />
          <Line
            dataTestAttrs={{component: 'LinearPolymerPreview', element: 'editOverlayBoundaryLine'}}
            startPoint={endPoint}
            endPoint={{x: endPoint.x, y: endPoint.y - EDIT_OVERLAY_HEIGHT}}
            stroke={boundaryLineColor}
            strokeWidth={3}
            strokeLinecap="round"
          />
        </>
      )}
    </g>
  );
}

/**
 * Sorts cut sites into two layers for rendering, an upper and lower layer. Any cut sites that do not
 * fit on these two layers will not be shown.
 */
function getCutSiteLayers(
  cutSites: Array<VisualSequenceCutSite>,
  seqLength: number
): Array<Array<VisualSequenceCutSite>> {
  // Calculates the index at which the cut site display text ends with some padding
  const cutSiteEndPos: (cutSite: VisualSequenceCutSite) => number = (cutSite) => {
    const pixelLength = getEnzyme(cutSite.enzymeId).name.length * 5 + 4;
    const endPosOffset = Math.ceil((pixelLength * seqLength) / AXIS_LENGTH);
    return Math.min(cutSite.position + endPosOffset, seqLength);
  };

  const {max, layers} = getFeatureLayers(
    cutSites.map((site) => ({...site, cid: String(site.position)})),
    (cutSite) => ({model: cutSite, pos: cutSite.position}),
    (cutSite) => ({model: cutSite, pos: cutSiteEndPos(cutSite)}),
    true
  );
  const cutSitesInLayers: Array<Array<VisualSequenceCutSite & {cid: string}>> = Array.from(
    {length: max + 1},
    () => []
  );
  for (const {model, layer} of layers) {
    cutSitesInLayers[layer].push(model);
  }
  // Return only two layers of cut sites and reverse so that the lower layer (the one with with positions
  // more to the right) is first if there are two layers
  return cutSitesInLayers.slice(0, 2).reverse();
}

/**
 * Renders one enzyme cut site on either the lower or upper layer.
 */
function CutSite({
  cutSite,
  layer,
  seqLength,
}: {
  cutSite: VisualSequenceCutSite;
  layer: number;
  seqLength: number;
}): JSX.Element {
  const {position, enzymeId} = cutSite;
  const startPoint = getLinearPointForIndexOnPreview(position, seqLength, AXIS_BOTTOM_OFFSET);
  const textPoint = {x: startPoint.x, y: layer === 0 ? LOWER_CUTSITE_Y : UPPER_CUTSITE_Y};
  return (
    <DisplayInfoLabel
      dataTestAttrs={{component: 'LinearPolymerPreview', element: 'cutSiteLabel', key: String(position)}}
      sequencePoint={startPoint}
      textPointInfo={{anchor: 'start', point: textPoint}}
      displayInfo={{enzymeId, bases: null}}
    />
  );
}

/**
 * A static, non-interactive preview of a linear polymer.
 */
export default function LinearPolymerPreview({
  polymer,
  annotationLayers,
}: {
  polymer: VisualPolymer;
  annotationLayers: Array<Array<VisualPolymerFeature>>;
}): JSX.Element {
  const {fragments, length, startIndex, overlays, cutSites} = polymer;
  const cutSiteLayers = getCutSiteLayers(cutSites, length);
  // Adjust viewbox to reduce unnecessary whitespace
  const viewBoxY = cutSites.length === 0 ? 30 : 0;
  const viewBoxHeight = annotationLayers.length === 0 ? 120 - viewBoxY : 150 - viewBoxY;

  const [nonHomologyFragments, homologyFragments] = partition(
    fragments,
    (fragment) => fragment.homologyRegionOtherColor == null
  );

  return (
    <svg
      {...getTestProps({component: 'LinearPolymerPreview', element: 'root'})}
      viewBox={`0 ${viewBoxY} ${WIDTH} ${viewBoxHeight}`}
      width="100%"
      fontFamily={FONT_FAMILY_MONOSPACED}
    >
      <Line
        dataTestAttrs={{component: 'LinearPolymerPreview', element: 'axisLine'}}
        startPoint={AXIS_LEFT_POINT}
        endPoint={AXIS_RIGHT_POINT}
        strokeWidth={AXIS_THICKNESS}
        stroke={GRAY_LIGHT}
      />
      {length > 0 && <AxisNumbering length={length} startIndex={startIndex} />}
      {nonHomologyFragments.map(({start, end, color}, index) => {
        const startPoint = getLinearPointForIndexOnPreview(start, length);
        const endPoint = getLinearPointForIndexOnPreview(sequtil.modelEndToRender(end, length), length);
        return (
          <Line
            dataTestAttrs={{component: 'LinearPolymerPreview', element: 'fragmentLine', index: String(index)}}
            key={`fragment-${start}-${end}`}
            startPoint={startPoint}
            endPoint={endPoint}
            strokeWidth={AXIS_THICKNESS}
            stroke={color}
          />
        );
      })}
      {homologyFragments.flatMap(({start, end, color, homologyRegionOtherColor}) => {
        const homologyStripeData = getLinearHomologyStripeData(
          {
            start,
            end,
            color,
            homologyRegionOtherColor: homologyRegionOtherColor!,
          },
          (basePair) => getLinearPointForIndexOnPreview(basePair, length).x
        );
        return homologyStripeData.map(({startPoint, endPoint, color}, i) => (
          <Line
            dataTestAttrs={{component: 'LinearPolymerPreview', element: 'homologyFragmentLine'}}
            key={`fragment-${start}-${end}-${i}`}
            startPoint={{x: startPoint, y: AXIS_Y_OFFSET}}
            endPoint={{x: endPoint, y: AXIS_Y_OFFSET}}
            strokeWidth={AXIS_THICKNESS}
            stroke={color}
          />
        ));
      })}
      <g>
        {cutSiteLayers.map((layer, layerIdx) =>
          layer.map((cutSite) => (
            <CutSite
              key={`cutsite-${cutSite.position}`}
              cutSite={cutSite}
              layer={layerIdx}
              seqLength={length}
            />
          ))
        )}
      </g>
      <g>
        {annotationLayers.map((layer, layerIdx) =>
          layer.map((annotation, idx) => (
            <Annotation
              key={`annotation-${layerIdx}-${idx}`}
              annotation={annotation}
              layer={layerIdx}
              seqLength={length}
            />
          ))
        )}
      </g>
      <g>
        {overlays.map((overlay, idx) => (
          <EditOverlay key={`overlay-${idx}`} overlay={overlay} seqLength={length} />
        ))}
      </g>
    </svg>
  );
}
